import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'Agilitie | Organisational Transformation Advisory Australia';
const DEFAULT_DESCRIPTION = 'Boutique advisory helping Australian organisations redesign operating models, leadership systems, and delivery environments for meaningful, lasting change.';
const SITE_URL = 'https://agilitie.com.au';
const DEFAULT_IMAGE = 'https://agilitie.com.au/og-image.jpg';

export const SEO = ({ 
  title, 
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  article = null
}) => {
  const fullTitle = title ? `${title} | Agilitie` : DEFAULT_TITLE;
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Agilitie" />
      <meta property="og:locale" content="en_AU" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Article specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:author" content={article.author} />
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
    </Helmet>
  );
};

// Structured Data for Organization
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Agilitie",
    "alternateName": "Agilitie Consulting",
    "url": "https://agilitie.com.au",
    "logo": "https://agilitie.com.au/logo.png",
    "description": "Boutique advisory helping Australian organisations redesign operating models, leadership systems, and delivery environments for meaningful, lasting change.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU"
    },
    "founder": {
      "@type": "Person",
      "name": "Nadir Khan"
    },
    "sameAs": [
      "https://www.linkedin.com/company/agilitie"
    ],
    "serviceArea": {
      "@type": "Country",
      "name": "Australia"
    },
    "knowsAbout": [
      "Organisational Transformation",
      "Operating Model Design",
      "Change Management",
      "Agile Transformation",
      "Delivery System Optimisation"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Consulting Service
export const ConsultingServiceSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Agilitie",
    "image": "https://agilitie.com.au/og-image.jpg",
    "priceRange": "$$$$",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU"
    },
    "url": "https://agilitie.com.au",
    "telephone": "",
    "description": "Organisational transformation advisory helping enterprises redesign operating models and delivery systems.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Transformation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Goldilocks Zone Advisory",
            "description": "Assess whether your environment supports transformation and design interventions that move toward productive balance."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Operating Model Design",
            "description": "Redesign operating models that align structure, accountability, and decision rights with strategic intent."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Transformation Assurance",
            "description": "Independent assurance to assess programme health, governance effectiveness, and strategic alignment."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Delivery System Optimisation",
            "description": "Design delivery systems that balance autonomy with alignment for effective work flow."
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Person (Founder)
export const PersonSchema = ({ name, jobTitle, description, image, sameAs = [] }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "jobTitle": jobTitle,
    "description": description,
    "image": image,
    "worksFor": {
      "@type": "Organization",
      "name": "Agilitie"
    },
    "sameAs": sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Article
export const ArticleSchema = ({ title, description, image, datePublished, author, url }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "datePublished": datePublished,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Agilitie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://agilitie.com.au/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Book
export const BookSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": "The Goldilocks Zone: How Leaders Build the Conditions for Continuous Evolution",
    "author": [
      {
        "@type": "Person",
        "name": "Nadir Khan"
      },
      {
        "@type": "Person",
        "name": "Craig Kimball"
      }
    ],
    "description": "A framework for organisational transformation, drawing from astrophysics the concept of a habitable zone — the narrow band of conditions where meaningful change can flourish.",
    "genre": "Business",
    "inLanguage": "en",
    "url": "https://thegoldilockszone.com.au/buy"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SEO;
